import {useAppSelector} from "@Hooks/selector/useAppSelector";
import {
    DepartureTimeSlotsType
} from "@SearchResults/features/sidebar/features/departureTimeFilters/types/DepartureTimeSlots.type";
import {
    DepartureTimeType
} from "@SearchResults/features/sidebar/features/departureTimeFilters/types/DepartureTime.type";
import setHours from "date-fns/setHours";
import addDays from "date-fns/addDays";
import RouteWithAddedPropsAndPricesType from "@Route/types/RouteWithAddedPropsAndPrices.type";
import getRouteDateTime from "@Route/functions/getRouteDateTime";
import isBefore from "date-fns/isBefore";
import isAfter from "date-fns/isAfter";
import isEqual from "lodash/isEqual";
import convertToServerTime from "@DateTime/functions/convertToServerTime";

export default function useFilterByDepartureTimeSlots() {
    const {filterDepartureTimeSlots}: {
        filterDepartureTimeSlots: DepartureTimeSlotsType
    } = useAppSelector((state) => state.filter);

    function parseDateTimeForDepartureTimeSlot(comparisonDate: Date) {
        const parseDateByLabel = (label: DepartureTimeType, comparisonDate: Date) => {
            // Create time slot boundaries explicitly in Europe/Zagreb timezone
            const createTimeSlot = (hour: number) => {
                // Get the date part in YYYY-MM-DD format from the comparisonDate
                const year = comparisonDate.getFullYear();
                const month = String(comparisonDate.getMonth() + 1).padStart(2, '0');
                const day = String(comparisonDate.getDate()).padStart(2, '0');
                const dateStr = `${year}-${month}-${day}`;
                
                // Parse as UTC and convert to Europe/Zagreb
                const utcTime = new Date(`${dateStr}T${hour.toString().padStart(2, '0')}:00:00Z`);
                return new Date(utcTime.toLocaleString("en-US", {timeZone: "Europe/Zagreb"}));
            };

            switch (label) {
                case 'morning':
                    return {
                        start: createTimeSlot(5),
                        end: createTimeSlot(12),
                    }
                case 'afternoon':
                    return {
                        start: createTimeSlot(12),
                        end: createTimeSlot(19),
                    }
                case 'evening':
                    return {
                        start: createTimeSlot(19),
                        end: (() => {
                            const year = comparisonDate.getFullYear();
                            const month = String(comparisonDate.getMonth() + 1).padStart(2, '0');
                            const day = String(comparisonDate.getDate()).padStart(2, '0');
                            const utcTime = new Date(`${year}-${month}-${day}T23:59:59Z`);
                            return new Date(utcTime.toLocaleString("en-US", {timeZone: "Europe/Zagreb"}));
                        })(),
                    }
                case 'night':
                    return {
                        start: createTimeSlot(0),
                        end: createTimeSlot(5),
                    }
                default:
                    return {
                        start: comparisonDate,
                        end: comparisonDate,
                    }
            }
        }

        return Object.entries(filterDepartureTimeSlots).filter((departureTimeSlot) => {
            if (departureTimeSlot[1]) {
                return departureTimeSlot[0]
            }
        }).map((departureTimeSlot) => (
            parseDateByLabel(departureTimeSlot[0] as DepartureTimeType, comparisonDate)
        ))
    }

    const filterByDepartureTimeSlots = (route: RouteWithAddedPropsAndPricesType) => {
        // Create dates explicitly in Europe/Zagreb timezone using toLocaleString
        const departureTimeStr = getRouteDateTime({
            route,
            type: "departure",
        })
        // Parse as UTC and convert to Europe/Zagreb
        const departureTime = new Date(departureTimeStr + 'Z')
        const departureTimeInZagreb = new Date(departureTime.toLocaleString("en-US", {timeZone: "Europe/Zagreb"}))
        
        const departureDateStr = route.travelInfo.departureStation.departureDate
        const departureDate = new Date(departureDateStr + 'T00:00:00Z')
        const departureDateInZagreb = new Date(departureDate.toLocaleString("en-US", {timeZone: "Europe/Zagreb"}))
        
        const validHours = parseDateTimeForDepartureTimeSlot(departureDateInZagreb)

        return validHours.some((date) => {
            return (isBefore(departureTimeInZagreb, date.end) && isAfter(departureTimeInZagreb, date.start)) || isEqual(departureTimeInZagreb, date.start) || isEqual(departureTimeInZagreb, date.end)
        })
    }

    return {
        filterDepartureTimeSlots,
        filterByDepartureTimeSlots,
        anySlotFiltered: Object.values(filterDepartureTimeSlots).some((slot) => slot),
    }
}